/**
 * @file utils/calculateGoalWeight.js
 * @description Calculate recommended goal weight based on body fat percentage estimation
 */

/**
 * Estimate body fat percentage using Deurenberg formula
 * @param {number} weight - Weight in kg
 * @param {number} heightCm - Height in centimeters
 * @param {number} age - Age in years
 * @param {string} gender - 'male' or 'female'
 * @returns {number} Estimated body fat percentage
 */
export function estimateBodyFat(weight, heightCm, age, gender = "male") {
  if (!weight || !heightCm || !age) return null;

  // Calculate BMI
  const heightM = heightCm / 100;
  const bmi = weight / (heightM * heightM);

  // Deurenberg formula
  let bodyFat;
  if (gender === "male") {
    bodyFat = 1.2 * bmi + 0.23 * age - 16.2;
  } else {
    bodyFat = 1.2 * bmi + 0.23 * age - 5.4;
  }

  // Clamp to reasonable ranges
  return Math.max(2, Math.min(50, bodyFat));
}

/**
 * Get healthy body fat ranges by activity level
 * @param {string} gender - 'male' or 'female'
 * @param {string} activityLevel - sedentary, lightly_active, moderately_active, very_active
 * @returns {object} Min and max healthy body fat %
 */
function getHealthyBFRange(gender, activityLevel) {
  const ranges = {
    male: {
      sedentary: { min: 18, max: 24 }, // Average man
      lightly_active: { min: 16, max: 22 }, // Light activity
      moderately_active: { min: 14, max: 20 }, // Regular training
      very_active: { min: 10, max: 18 }, // Athlete/frequent training
    },
    female: {
      sedentary: { min: 25, max: 31 }, // Average woman
      lightly_active: { min: 23, max: 29 }, // Light activity
      moderately_active: { min: 21, max: 27 }, // Regular training
      very_active: { min: 18, max: 24 }, // Athlete/frequent training
    },
  };

  const genderKey = gender === "male" ? "male" : "female";
  return (
    ranges[genderKey][activityLevel] || ranges[genderKey].moderately_active
  );
}

/**
 * Calculate recommended goal weight based on body composition
 * @param {object} profile - User profile data
 * @param {number} profile.weight - Current weight in kg
 * @param {number} profile.height - Height in cm
 * @param {number} profile.age - Age in years
 * @param {string} profile.gender - 'male' or 'female'
 * @param {string} profile.activityLevel - Activity level
 * @returns {object} Recommended goal with details
 */
export function calculateRecommendedGoal(profile) {
  const {
    weight,
    height,
    age,
    gender = "male",
    activityLevel = "moderately_active",
  } = profile;

  if (!weight || !height || !age) {
    return null;
  }

  // Estimate current body fat
  const currentBF = estimateBodyFat(weight, height, age, gender);

  // Get healthy range for their activity level
  const healthyRange = getHealthyBFRange(gender, activityLevel);

  // Calculate LBM (Lean Body Mass)
  const lbm = weight * (1 - currentBF / 100);

  // Calculate goal weight at middle of healthy range
  const targetBF = (healthyRange.min + healthyRange.max) / 2;
  const goalWeight = Math.round(lbm / (1 - targetBF / 100));

  // Calculate aggressive goal (lower end of range)
  const aggressiveGoal = Math.round(lbm / (1 - healthyRange.min / 100));

  // Calculate conservative goal (higher end of range)
  const conservativeGoal = Math.round(lbm / (1 - healthyRange.max / 100));

  return {
    currentBF: Math.round(currentBF * 10) / 10,
    goalWeight, // Recommended default goal
    aggressiveGoal, // Leaner option
    conservativeGoal, // More muscle-preserving option
    healthyRange,
    targetBF: Math.round(targetBF),
    lbm: Math.round(lbm * 10) / 10,
  };
}

/**
 * Example usage:
 *
 * // Male user: 180cm, 90kg, 27 years, moderately active
 * const goal = calculateRecommendedGoal({
 *   weight: 90,
 *   height: 180,
 *   age: 27,
 *   gender: 'male',
 *   activityLevel: 'moderately_active'
 * });
 *
 * // Returns:
 * // {
 * //   currentBF: 27.5,          // Estimated current body fat %
 * //   goalWeight: 79,           // Recommended (14-20% BF range, target 17%)
 * //   aggressiveGoal: 75,       // Leaner (14% BF)
 * //   conservativeGoal: 86,     // More muscle (20% BF)
 * //   healthyRange: { min: 14, max: 20 },
 * //   targetBF: 17,
 * //   lbm: 65.3,                // Lean body mass
 * // }
 */
